// Leads-only classification (constitution §6.4, plan §6). The API filters these on every path for
// roles outside LEADS_ROLES; the leak test looks for the same markers.

export const LEADS_RECORD_TYPES = [
  'risks',
  'acceptances',
  'asks',
  'decisions',
  'kpis',
  'gates',
  'capacity',
  'questions',
] as const;
export type LeadsRecordType = (typeof LEADS_RECORD_TYPES)[number];

export function isLeadsRecordType(value: string): value is LeadsRecordType {
  return (LEADS_RECORD_TYPES as readonly string[]).includes(value);
}

// History entity types (events.entity_type, freeze_items.entity_type) that hold Leads records.
// Readers of history, "as of" and freezes hide them from team roles (security.md A-R7). Both the
// record-type names above and the table names are listed, so either naming is covered.
const LEADS_ENTITY_TYPES: readonly string[] = [
  ...LEADS_RECORD_TYPES,
  'risk',
  'acceptance',
  'ask',
  'ask_decision',
  'ask_decisions',
  'decision',
  'decisions_taken',
  'kpi',
  'kpi_reading',
  'kpi_readings',
  'gate',
  'question',
  'open_question',
  'open_questions',
  'leads',
];

export function isLeadsEntityType(entityType: string): boolean {
  return LEADS_ENTITY_TYPES.includes(entityType.toLowerCase());
}

// Free-text fields of team-visible records that may quote Leads ids. Serializers pass them through
// redactLeadsFields() for helper and attester roles. The list covers every field where the real
// pack (1.3.4) quotes a Leads id; list and object fields are redacted string by string.
export const LEADS_QUOTING_FIELDS = {
  task: [
    'title',
    'description',
    'notes',
    'updateNotes',
    'blockedReason',
    'deferredReason',
    'existingAssetReused',
    'evidenceArtifact',
  ],
  // Pack names (do, how, check, who) and the task_steps column names.
  step: ['do', 'how', 'check', 'who', 'doText', 'howText', 'checkText', 'whoHint'],
  runbook: [
    'goal',
    'beforeYouStart',
    'doneWhen',
    'evidence',
    'rollback',
    'pitfalls',
    'escalateIf',
    'done',
  ],
  safeguard: ['title', 'plan', 'tools', 'evidence', 'quick', 'licence', 'target', 'bundle'],
  evidenceLine: ['title', 'artifact', 'note', 'plan', 'claimed', 'bundle'],
  evidenceRecord: ['note'],
  checklist: ['title', 'purpose', 'owner', 'cadence'],
  checklistItem: ['section', 'text', 'who'],
  checklistResult: ['note'],
  comment: ['body'],
  milestone: ['milestone'],
} as const satisfies Readonly<Record<string, readonly string[]>>;
export type LeadsQuotingEntity = keyof typeof LEADS_QUOTING_FIELDS;

// Leads ids as the program writes them. Task ids (T-OPS-026) and safeguards (CIS 4.1) never match.
// Matching ignores case, so lowercase forms ("exc-a1", "r19", "k6") are redacted too; the cost is
// some over-redaction of short tokens, accepted for team roles (security.md B-R9). The patterns stay
// private: a shared global RegExp keeps lastIndex between .test() calls (B-R12).
const LEADS_ID_PATTERNS: readonly RegExp[] = [
  // Exception / acceptance id: matches "EXC-A1", "exc-a10"; rejects "EXC-1", "XEXC-A1".
  // https://regex101.com/?regex=%5CbEXC-%5BA-Z%5D%5Cd%7B1%2C3%7D%5Cb&flags=gi&testString=EXC-A1%0Aexc-a10%0AEXC-1%0AXEXC-A1
  /\bEXC-[A-Z]\d{1,3}\b/gi,
  // Risk id: matches "R19", "R-19", "r19"; rejects "RB-01", "PR19", "R1234".
  // https://regex101.com/?regex=%5CbR-%3F%5Cd%7B1%2C3%7D%5Cb&flags=gi&testString=R19%0AR-19%0Ar19%0ARB-01%0APR19%0AR1234
  /\bR-?\d{1,3}\b/gi,
  // KPI id, alone, as a range or after "KPI": matches "K6", "KPI K1", "K1-K10", "k5"; rejects "K123",
  // "k8s", "4K", "KB5034441".
  // https://regex101.com/?flavor=javascript&regex=%5Cb%28%3F%3AKPIs%3F%5Cs%2B%29%3FK-%3F%5Cd%7B1%2C2%7D%5Cb%28%3F%3A%5Cs%2A%5B-%E2%80%93%5D%5Cs%2AK%3F%5Cd%7B1%2C2%7D%5Cb%29%3F&flags=gi&testString=K6%0AKPI%20K1%0AK1-K10%0Ak5%0AK123%0Ak8s%0A4K%0AKB5034441
  /\b(?:KPIs?\s+)?K-?\d{1,2}\b(?:\s*[-–]\s*K?\d{1,2}\b)?/gi,
  // Ask number, alone or in a list, also as "D&A #n" (Decisions & Asks): matches "ask 22",
  // "Ask #22", "asks 0-5, 9-18 and 22", "D&A #11 and #13"; rejects "task 22", "asking", and stops
  // before "Fri" in "ask 3, Fri 16-Oct".
  // https://regex101.com/?flavor=javascript&regex=%5Cb%28%3F%3Aasks%3F%7CD%26A%29%5Cs%2A%23%3F%5Cs%2A%5Cd%7B1%2C3%7D%5Cb%28%3F%3A%5Cs%2A%28%3F%3A%5B-%E2%80%93%2C%26%2F%5D%7Cand%7Cor%29%5Cs%2A%23%3F%5Cs%2A%5Cd%7B1%2C3%7D%5Cb%29%2A&flags=gi&testString=ask%2022%0AAsk%20%2322%0Aasks%200-5%2C%209-18%20and%2022%0AD%26A%20%2311%20and%20%2313%0Atask%2022%0Aasking%0Aask%203%2C%20Fri%2016-Oct
  /\b(?:asks?|D&A)\s*#?\s*\d{1,3}\b(?:\s*(?:[-–,&/]|and|or)\s*#?\s*\d{1,3}\b)*/gi,
  // Fixture and leak-test marker: matches "LEADS-MARKER-7", "leads-marker-7"; rejects "LEADS-MARKER-x".
  // https://regex101.com/?regex=%5CbLEADS-MARKER-%5Cd%2B%5Cb&flags=gi&testString=LEADS-MARKER-7%0Aleads-marker-7%0ALEADS-MARKER-x
  /\bLEADS-MARKER-\d+\b/gi,
];

export const LEADS_REDACTION = '[Leads only]';

// Whole-string exception id: matches "EXC-A1"; rejects "EXC-A1 ", "exc-a1", "R19".
// https://regex101.com/?regex=%5EEXC-%5BA-Z%5D%5Cd%7B1%2C3%7D%24&flags=&testString=EXC-A1%0AEXC-A1%20%0Aexc-a1%0AR19
const EXCEPTION_ID = /^EXC-[A-Z]\d{1,3}$/;

export function isExceptionId(value: string): boolean {
  return EXCEPTION_ID.test(value);
}

export function containsLeadsRef(text: string): boolean {
  return redactLeadsRefs(text) !== text;
}

export function redactLeadsRefs(text: string): string {
  return LEADS_ID_PATTERNS.reduce((out, re) => out.replace(re, LEADS_REDACTION), text);
}

// Redacts every string inside a JSON-like value (arrays and plain objects), keeping its shape.
export function redactLeadsDeep<T>(value: T): T {
  if (typeof value === 'string') return redactLeadsRefs(value) as T;
  if (Array.isArray(value)) return value.map((v: unknown) => redactLeadsDeep(v)) as T;
  if (value !== null && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]: [string, unknown]) => [k, redactLeadsDeep(v)]),
    ) as T;
  }
  return value;
}

// Returns a shallow copy with the entity's quoting fields redacted (strings inside list and object
// fields too); other fields are untouched.
export function redactLeadsFields<T extends Readonly<Record<string, unknown>>>(
  entity: LeadsQuotingEntity,
  record: T,
): T {
  const fields: readonly string[] = LEADS_QUOTING_FIELDS[entity];
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [
      key,
      fields.includes(key) ? redactLeadsDeep(value) : value,
    ]),
  ) as T;
}
